/**
 * User validation utilities for consistent validation across user forms.
 */

import { USERNAME_REGEX, EMAIL_REGEX } from '../constants/constants';

/**
 * Validate username field.
 * @param {string} username - The username to validate
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validateUsername = (username) => {
  const errors = [];
  if (!username || username.length < 3) {
    errors.push('Username must be at least 3 characters.');
  }
  if (username && !USERNAME_REGEX.test(username)) {
    errors.push('Username must start with a letter and contain only letters, numbers, and periods.');
  }
  return errors;
};

/**
 * Validate email field.
 * @param {string} email - The email to validate
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validateEmail = (email) => {
  const errors = [];
  if (!email || !EMAIL_REGEX.test(email)) {
    errors.push('A valid email address is required.');
  }
  return errors;
};

/**
 * Validate display name field.
 * @param {string} displayName - The display name to validate
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validateDisplayName = (displayName) => {
  const errors = [];
  if (!displayName) {
    errors.push('Display name is required.');
  }
  return errors;
};

/**
 * Validate agency name field.
 * @param {string} agencyName - The agency name to validate
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validateAgencyName = (agencyName) => {
  const errors = [];
  if (!agencyName || agencyName.length < 2) {
    errors.push('Agency name must be at least 2 characters.');
  }
  return errors;
};

/**
 * Validate password fields.
 * @param {string} password - The password to validate
 * @param {string} confirmPassword - The confirmation password
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validatePassword = (password, confirmPassword) => {
  const errors = [];
  if (!password || password.length < 6) {
    errors.push('Password must be at least 6 characters.');
  }
  if (password !== confirmPassword) {
    errors.push('Passwords do not match.');
  }
  return errors;
};

/**
 * Validate user form for editing (no password).
 * @param {Object} formData - Form data object
 * @param {string} formData.username - Username
 * @param {string} formData.displayName - Display name
 * @param {string} formData.emailAddress - Email address
 * @param {string} formData.agencyName - Agency name
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validateEditUserForm = (formData) => {
  return [
    ...validateUsername(formData.username),
    ...validateDisplayName(formData.displayName),
    ...validateEmail(formData.emailAddress),
    ...validateAgencyName(formData.agencyName),
  ];
};

/**
 * Validate user form for creation (with password).
 * @param {Object} formData - Form data object
 * @param {string} formData.username - Username
 * @param {string} formData.password - Password
 * @param {string} formData.confirmPassword - Confirmation password
 * @param {string} formData.displayName - Display name
 * @param {string} formData.emailAddress - Email address
 * @param {string} formData.agencyName - Agency name
 * @param {boolean} usernameChecked - Whether the username has been checked
 * @param {boolean} usernameAvailable - Whether the username is available
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validateCreateUserForm = (formData, usernameChecked, usernameAvailable) => {
  const errors = [
    ...validateUsername(formData.username),
    ...validatePassword(formData.password, formData.confirmPassword),
    ...validateDisplayName(formData.displayName),
    ...validateEmail(formData.emailAddress),
    ...validateAgencyName(formData.agencyName),
  ];

  if (usernameChecked && !usernameAvailable) {
    errors.push('Username is already in use.');
  }

  return errors;
};

/**
 * Validate password change form.
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Confirmation password
 * @returns {string[]} Array of error messages (empty if valid)
 */
export const validatePasswordChangeForm = (newPassword, confirmPassword) => {
  return validatePassword(newPassword, confirmPassword);
};
